import { authQueryKeys } from '@/config/queryKeys';
import { ROUTE_PATHS, publicRoutes } from '@/constants';
import { canNavigateTo } from '@/lib/navigation-guards';
import { handleUnauthorized } from '@/lib/shared/auth-shared';
import type { User } from '@/types';
import type { AuthData, AuthUtils } from '@/types/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useAuthQuery } from '../api/index.ts';

function useAuthStatus(): AuthData {
  const isPublicRoute = publicRoutes.includes(window.location.pathname);

  const userQuery = useAuthQuery({ enabled: !isPublicRoute });

  const queryClient = useQueryClient();
  const previousAuthStatus = useRef<boolean | null>(null);

  // Detect when user is unauthenticated and redirect to login
  useEffect(() => {
    const isAuthenticated = userQuery.data !== null && userQuery.data !== undefined && !userQuery.isPending;

    // Skip redirect if we're still loading or already on a public route
    if (userQuery.isPending || publicRoutes.includes(window.location.pathname)) {
      return;
    }

    // After a server-side login redirect the session cookie may not be visible
    // to the very first /auth/me request (timing / cookie propagation race).
    // Suppress the redirect so the query can revalidate naturally instead of
    // bouncing the user back to the login page unnecessarily.
    const isPostLoginCallback = window.location.search.includes('login=success');

    // Case 1: Session expiration - user was authenticated and now is not
    const sessionExpired = previousAuthStatus.current === true && !isAuthenticated;

    // Case 2: Initial unauthenticated state - user was never authenticated
    const initiallyUnauthenticated = previousAuthStatus.current === null && !isAuthenticated;

    // Redirect to log in if unauthenticated (either session expired or never logged in).
    // Skip during the post-login callback window to avoid a redirect loop.
    if ((sessionExpired || initiallyUnauthenticated) && !userQuery.isPending && !isPostLoginCallback) {
      // Skip redirect if we're already on the login page
      // Use router.state.location directly to avoid hook context issues during initialization
      if (window.location.pathname !== ROUTE_PATHS.LOGIN) {
        // Avoid invoking unauthorized handling during a recent logout window
        // or when the URL contains login=success (post-login callback).
        try {
          const lastLogout = Number(sessionStorage.getItem('__last_logout_ts')) || 0;
          const now = Date.now();
          const RECENT_LOGOUT_MS = 15000;
          const isRecentLogout = lastLogout && now - lastLogout < RECENT_LOGOUT_MS;

          // Respect any nav lock that may have been set after login to allow
          // the session cookie to propagate. If a nav lock exists and is
          // still in the future, skip invoking the unauthorized handler.
          const rawLock = sessionStorage.getItem('__nav_lock_until');
          const lockUntil = rawLock ? Number(rawLock) : 0;
          const isNavLocked = lockUntil && !Number.isNaN(lockUntil) && now < lockUntil;

          // canNavigateTo is intentionally NOT called here. Calling it would
          // consume the sessionStorage navigation token, causing the identical
          // check inside router.tsx's handleUnauthorized (_handler) to return
          // false ~1ms later and silently drop the redirect. The canonical
          // canNavigateTo gate lives in _handler; onUnauthorized's own 500ms
          // debounce (INVOCATION_WINDOW_MS) is sufficient call-site protection.
          if (!isRecentLogout && !isNavLocked) {
            handleUnauthorized();
          }
        } catch {
          // If storage access fails, fall back to previous behavior.
          if (canNavigateTo(ROUTE_PATHS.LOGIN)) {
            handleUnauthorized();
          }
        }
      }
    }

    // Update the previous status
    if (!userQuery.isPending) {
      previousAuthStatus.current = isAuthenticated;
    }
    // Clear last logout marker when we become authenticated
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem('__last_logout_ts');
      } catch {}
    }
  }, [userQuery.data, userQuery.isPending]);

  const utils: AuthUtils = {
    signOut: () => {
      queryClient.setQueryData(authQueryKeys.me(), null);
    },
    userQuery,
  };

  // Check if query is still loading
  if (userQuery.isPending) {
    return { ...utils, user: null, status: 'PENDING' };
  }

  // Check if query failed with an error
  if (userQuery.isError) {
    return { ...utils, user: null, status: 'UNAUTHENTICATED' };
  }

  // Check if we have valid user data
  if (userQuery.data !== null && userQuery.data !== undefined) {
    return { ...utils, user: userQuery.data, status: 'AUTHENTICATED' };
  }

  // No data or null data means unauthenticated
  return { ...utils, user: null, status: 'UNAUTHENTICATED' };
}

export { useAuthStatus };
export type { AuthData, User };
